// src/components/PsychometricTests.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Modal, Alert, RadioGroup, Radio, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from '@mui/material';
import { useTranslation } from '../i18n/i18n';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';

function PsychometricTests({ userEmail }) {
  const { t } = useTranslation();
  const [tests, setTests] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [title, setTitle] = useState('');
  const [editingTest, setEditingTest] = useState(null);
  const [viewingTest, setViewingTest] = useState(null);
  const [testAvailable, setTestAvailable] = useState(false);
  const [showIntroduction, setShowIntroduction] = useState(false);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination state for viewing test questions
  const [currentPage, setCurrentPage] = useState(0);
  const questionsPerPage = 5;

  // Ref for modal content to control scroll
  const modalContentRef = useRef(null);

  // State for X button hover
  const [closeHover, setCloseHover] = useState(false);

  const fetchUserTest = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/psychometric_tests/${userEmail}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setTestAvailable(true); // Indicate that a test is available
        setViewingTest(null); // Ensure the modal doesn't open automatically
      } else {
        setTestAvailable(false); // No test available
      }
    } catch (error) {
      console.error('Error fetching user test:', error);
    }
  };

  // Fetch the user's psychometric test on component mount
  useEffect(() => {
    fetchUserTest();
  }, [userEmail]);

  const handleOpenTest = () => {
    if (testAvailable) {
      // Show introduction modal first
      setShowIntroduction(true);
      // Fetch the test data in background
      fetch(`${API_ENDPOINTS.BASEURL}/api/psychometric_tests/${userEmail}`, {
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((data) => setViewingTest(data))
        .catch((error) => console.error('Error fetching test data:', error));
    }
  };

  const handleStartTest = () => {
    setShowIntroduction(false); // Close introduction
    // Test data should already be loaded from handleOpenTest
  };

  // Helper to clear answers from localStorage/sessionStorage
  const clearCachedAnswers = () => {
    try {
      localStorage.removeItem('psychometric_answers');
      sessionStorage.removeItem('psychometric_answers');
    } catch (e) {}
  };

  const handleCancelTest = () => {
    setShowIntroduction(false);
    setViewingTest(null); // Clear the test data
    setCurrentPage(0); // Reset pagination
    setAnswers({}); // Clear any answers
    clearCachedAnswers();
  };

  // Show close dialog when user tries to close test modal
  const handleRequestCloseTest = () => {
    setShowCloseDialog(true);
  };

  // Confirm close: actually close and clear answers
  const handleConfirmCloseTest = () => {
    setShowCloseDialog(false);
    handleCancelTest();
  };

  // Cancel close: stay in test
  const handleCancelCloseTest = () => {
    setShowCloseDialog(false);
  };

  const handleSubmitTest = async () => {
    if (isSubmitting) return;
    const pageIncomplete = currentQuestions.some((q) => !answers[q.question]);
    if (pageIncomplete) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASEURL}/api/psychometric_tests/${userEmail}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers }),
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const message = data.message ? (t(data.message) || data.message) : t('psychometric_submit_success');
        setSnackbar({ open: true, message, severity: 'success' });
        setViewingTest(null); // Clear the test after submission
        setTestAvailable(false); // Mark the test as unavailable
      } else {
        const message = data.message ? (t(data.message) || data.message) : t('psychometric_submit_failed');
        setSnackbar({ open: true, message, severity: 'error' });
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      const message = t('psychometric_submit_error') || 'An error occurred while submitting the test.';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  const currentQuestions = viewingTest
    ? viewingTest.questions.slice(
        currentPage * questionsPerPage,
        (currentPage + 1) * questionsPerPage
      )
    : [];

  // Scroll modal to top on page change
  useEffect(() => {
    if (viewingTest && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [currentPage, viewingTest]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>{t('psychometric_tests_title')}</Typography>

      {testAvailable ? (
        <Box 
          mt={2} 
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: '#f8f9ff',
            border: '1px solid #e0e4f6',
            boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
            maxWidth: 800,
            mx: 'auto',
            textAlign: 'center'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" color="primary" sx={{ mb: 2, fontWeight: 500 }}>
              ⭕{t('psychometric_tests_pending')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {t('psychometric_tests_pending_message')}
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleOpenTest}
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 500,
                '&:hover': {
                  //transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                }
              }}
            >
              {t('psychometric_tests_start_button')}
            </Button>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 4, 
            justifyContent: 'center',
            mt: 4,
            pt: 4,
            borderTop: '1px solid #e0e4f6'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">{t('psychometric_tests_estimated_time_label')}</Typography>
              <Typography variant="h6">{t('psychometric_tests_estimated_time_value')}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">{t('psychometric_tests_questions_label')}</Typography>
              <Typography variant="h6">{t('psychometric_tests_questions_value')}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">{t('psychometric_tests_completion_label')}</Typography>
              <Typography variant="h6">{t('psychometric_tests_completion_value')}</Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box 
          mt={2}
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: '#f8f9ff',
            border: '1px solid #e0e4f6',
            boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
            maxWidth: 640,
            mx: 'auto',
            textAlign: 'left',
            display: 'flex',
            gap: 2,
            alignItems: 'center'
          }}
        >
          <Box sx={{ flexShrink: 0, fontSize: '1.6rem' }}>✅</Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('psychometric_tests_submitted_title') || 'Psychometric Test Submitted'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('psychometric_tests_submitted_message') || "You've already submitted your psychometric test. It will be reviewed by our HR team — no further action is needed."}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Introduction Modal */}
      <Modal 
        open={showIntroduction} 
        onClose={handleCancelTest}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            maxWidth: 'calc(100% - 24px)',
            maxHeight: 'calc(100% - 24px)',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
          }}
        >
          <Typography variant="h5" sx={{ mb: 3, textAlign: 'center', color: 'primary.main' }}>
            {t('psychometric_intro_title')}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('psychometric_intro_description')}
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Typography component="ul" sx={{ pl: 2 }}>
              <li>{t('psychometric_intro_trait_problemsolving')}</li>
              <li>{t('psychometric_intro_trait_decisionmaking')}</li>
              <li>{t('psychometric_intro_trait_workpreferences')}</li>
              <li>{t('psychometric_intro_trait_teamcollaboration')}</li>
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {t('psychometric_intro_instructions_title')}
          </Typography>
          
          <Box sx={{ mb: 4, bgcolor: '#f8f9ff', p: 2, borderRadius: 2 }}>
            <Typography component="ul" sx={{ pl: 2 }}>
              <li>{t('psychometric_intro_instruction_honest')}</li>
              <li>{t('psychometric_intro_instruction_bestdescribes')}</li>
              <li>{t('psychometric_intro_instruction_nextbutton')}</li>
              <li>{t('psychometric_intro_instruction_noback')}</li>
              <li>{t('psychometric_intro_instruction_onesitting')}</li>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleCancelTest}
              sx={{ minWidth: 120 }}
            >
              {t('cancel')}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleStartTest}
              sx={{ 
                minWidth: 120,
                '&:hover': {
                  //transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                }
              }}
            >
              {t('psychometric_intro_begin_button')}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Test Modal */}
      {viewingTest && typeof viewingTest === 'object' && !showIntroduction && (
        <Modal open={true} onClose={handleRequestCloseTest}>
          <Box
            ref={modalContentRef}
            sx={{
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 3,
              width: 700,
              margin: 'auto',
              mt: 5,
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: 24,
              position: 'relative',
            }}
          >
            {/* Close X button with hover effect and Quit text */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              onMouseEnter={() => setCloseHover(true)}
              onMouseLeave={() => setCloseHover(false)}
              onClick={handleRequestCloseTest}
              aria-label="close"
            >
              <span
                style={{
                  fontSize: closeHover ? 32 : 24,
                  fontWeight: 'bold',
                  color: '#d32f2f', // MUI error.main
                  transition: 'all 0.2s',
                  transform: closeHover ? 'scale(1.25)' : 'scale(1)',
                  marginRight: closeHover ? 8 : 0,
                }}
              >
                &times;
              </span>
              {closeHover && (
                <Typography
                  sx={{
                    color: '#d32f2f',
                    fontWeight: 600,
                    fontSize: '1.05rem',
                    transition: 'opacity 0.2s',
                    opacity: closeHover ? 1 : 0,
                  }}
                >
                  {t('exit') || 'Exit'}
                </Typography>
              )}
            </Box>

            <Typography variant="h6">{viewingTest.title}</Typography>

            {/* Display paginated questions with numeration */}
            {currentQuestions.map((q, index) => {
              // Calculate the absolute question number
              const questionNumber = currentPage * questionsPerPage + index + 1;
              return (
                <Box key={index} mt={2}>
                  <Typography
                    sx={{
                      mb: 2,
                      fontSize: '1.15rem',
                      fontWeight: 600,
                      color: 'primary.main',
                      textAlign: 'left'
                    }}
                  >
                    {questionNumber}. {q.question}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <RadioGroup
                      row
                      value={answers[q.question] || ''}
                      onChange={(e) =>
                        setAnswers({ ...answers, [q.question]: e.target.value })
                      }
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 3,
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      {q.options.map((option, i) => (
                        <Box key={i} sx={{ mx: 1 }}>
                          <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Radio
                              value={option.answer}
                              checked={answers[q.question] === option.answer}
                              sx={{
                                color: 'primary.main',
                                '&.Mui-checked': {
                                  color: 'secondary.main',
                                },
                                transform: 'scale(1.3)',
                                mb: 0.5
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '0.78rem',
                                fontWeight: answers[q.question] === option.answer ? 600 : 400,
                                color: answers[q.question] === option.answer ? 'secondary.main' : 'text.primary',
                                textAlign: 'center',
                                px: 0.5,
                                maxWidth: 80,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.15,
                              }}
                            >
                              {option.answer}
                            </Typography>
                          </label>
                        </Box>
                      ))}
                    </RadioGroup>
                  </Box>
                </Box>
              );
            })}

            {/* Pagination Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
              >
                {t('previous')}
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
{(() => {
                  const pageIncomplete = currentQuestions.some((q) => !answers[q.question]);
                  const tooltipMsg = pageIncomplete ? (t('psychometric_answer_all') || 'Please answer all questions before continuing') : '';
                  return currentPage < Math.ceil(viewingTest.questions.length / questionsPerPage) - 1 ? (
                    <Tooltip title={tooltipMsg} placement="top" arrow>
                      <span>
                        <Button
                          variant="contained"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={pageIncomplete}
                        >
                          {t('next')}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title={tooltipMsg} placement="top" arrow>
                      <span>
                        <Button
                          variant="contained"
                          onClick={handleSubmitTest}
                          disabled={pageIncomplete || isSubmitting}
                        >
                          {isSubmitting ? (t('submitting') || 'Submitting...') : t('submit')}
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()}
              </Box>
            </Box>
            {/* Page indicator */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {currentPage + 1}/{Math.ceil(viewingTest.questions.length / questionsPerPage)}
              </Typography>
            </Box>
          </Box>
        </Modal>
      )}

      {/* Close confirmation dialog (moved outside Modal) */}
      <Dialog open={showCloseDialog} onClose={handleCancelCloseTest}>
        <DialogTitle>{t('psychometric_close_warning_title') || 'Leave Test?'}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('psychometric_close_warning_message') || 'If you leave, your progress will be lost and you will need to retake the exam from scratch. Are you sure you want to quit?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCloseTest} color="primary">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmCloseTest} color="error" variant="contained">
            {t('exit') || 'Exit'}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default PsychometricTests;
